import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import React from 'react'

import RouteChip from 'components/customer-details/content/work-orders/common/RouteChip'
import UnassignedChip from 'components/customer-details/content/work-orders/common/UnassignedChip'
import ServiceActionTypeHelper from 'components/services/ServiceActionTypeHelper'
// eslint-disable-next-line import/no-cycle
import WorkOrderStatusChip from 'components/work-order/WorkOrderStatusChip'
import { getWorkOrderDetailsPageUrl } from 'router/routes'

import type { ListItemTextProps } from '@mui/material/ListItemText'
import type { SxProps, Theme } from '@mui/material/styles'

// Callers hand over whichever work-order shape their endpoint returns, hence both route spellings.
type ListWorkOrder = {
  id?: string
  workOrderId?: string
  workOrderNumber?: string
  routeId?: string
  routeName?: string
  routeColor?: string
  route?: { id?: string; name?: string; color?: string }
  sequence?: number
  serviceDate?: string
  serviceActionName?: string
  eventActionType?: string
  serviceType?: string
  completionStatus?: string | null
  workOrderStatus?: string | null
  status?: string | { name?: string }
  intentStatus?: string | null
  servicedAt?: string
  servicedByFirstName?: string | null
  servicedByLastName?: string | null
}

type Props = {
  workOrder: ListWorkOrder
  serviceType?: string
  formattedDate?: string
  divider?: boolean
  sx?: SxProps<Theme>
  listItemSx?: SxProps<Theme>
  primaryTypographyProps?: ListItemTextProps['primaryTypographyProps']
  disableRedirect?: boolean
  linkToDetails?: boolean
}

const asArray = (sx?: SxProps<Theme>) => (Array.isArray(sx) ? sx : [sx])

const WorkOrderListItem: React.FC<Props> = ({
  workOrder,
  serviceType,
  formattedDate,
  divider = false,
  sx,
  listItemSx,
  primaryTypographyProps = { variant: 'h6', fontWeight: 500 },
  disableRedirect = false,
  linkToDetails = false,
}) => {
  const routeId = workOrder.routeId || workOrder.route?.id
  const statusName = typeof workOrder.status === 'string' ? workOrder.status : workOrder.status?.name
  const workOrderStatus = workOrder.completionStatus || workOrder.workOrderStatus || statusName || undefined
  const workOrderId = workOrder.id || workOrder.workOrderId
  const servicedBy = `${workOrder.servicedByFirstName ?? ''} ${workOrder.servicedByLastName ?? ''}`.trim()

  return (
    <List dense disablePadding sx={[{ width: '100%' }, ...asArray(sx)]}>
      <ListItem disableGutters sx={[{ gap: 1 }, ...asArray(listItemSx)]}>
        <ListItemText
          primary={
            linkToDetails && workOrderId ? (
              <Link
                color="info.light"
                href={getWorkOrderDetailsPageUrl(workOrderId)}
                rel="noopener noreferrer"
                target="_blank"
                underline="hover"
              >
                {workOrder.workOrderNumber}
              </Link>
            ) : (
              workOrder.workOrderNumber
            )
          }
          primaryTypographyProps={primaryTypographyProps}
          secondary={formattedDate}
          sx={{ m: 0 }}
        />
        <ListItemIcon sx={{ minWidth: 'unset' }}>
          {routeId ? (
            <RouteChip
              disableRedirect={disableRedirect}
              routeColor={workOrder.routeColor || workOrder.route?.color}
              routeName={workOrder.routeName || workOrder.route?.name}
              sequence={workOrder.sequence}
              serviceDate={workOrder.serviceDate}
            />
          ) : (
            <UnassignedChip />
          )}
        </ListItemIcon>
      </ListItem>
      <ListItem disableGutters divider={divider} sx={[{ gap: 1, justifyContent: 'space-between' }, ...asArray(listItemSx)]}>
        <ListItemIcon sx={{ minWidth: 'unset', display: 'flex', gap: 1, alignItems: 'center' }}>
          <ServiceActionTypeHelper
            eventActionType={workOrder.eventActionType}
            serviceActionType={workOrder.serviceActionName}
            serviceType={serviceType || workOrder.serviceType}
          />
        </ListItemIcon>
        <ListItemIcon sx={{ minWidth: 'unset' }}>
          <WorkOrderStatusChip
            intentStatus={workOrder.intentStatus ?? undefined}
            servicedAt={workOrder.servicedAt}
            servicedBy={servicedBy}
            status={workOrderStatus}
            workOrderId={workOrderId}
          />
        </ListItemIcon>
      </ListItem>
    </List>
  )
}

export default React.memo(WorkOrderListItem)
